import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import React, { Component } from 'react';
import type { QnAStateType } from '../reducers/index';
import { QuestionStyleContext, defaultQuestionStyles } from '../question-style-context';
import { getTopQuestionIds } from '../reducers/selectors';
import { selectAnswer, removeAnswer, updateNote, updateAnswers, type UserAnswerMapT } from '../reducers/answers';
import { type QuestionsType } from '../reducers';
import Question from './questions/Question';
import QuestionList from './common/QuestionList';
function addShowErrorsToQuestions(questions: QuestionsType): QuestionsType {
  const updatedQuestions = cloneDeep(questions);
  Object.keys(updatedQuestions).forEach(questionId => {
    updatedQuestions[questionId].showValidationErrors = true;
  });
  return updatedQuestions;
}

// Utility function to apply focus animation to a node
function applyFocusAnimation(node: HTMLElement): void {
  node.style.animation = 'qna-focus-pulse 1s ease-in-out 3';

  // Remove animation style after completion
  const handleAnimationEnd = () => {
    node.style.animation = '';
    node.removeEventListener('animationend', handleAnimationEnd);
  };
  node.addEventListener('animationend', handleAnimationEnd);
}
type Props = {
  state: QnAStateType;
  updateAnswers?: (a: UserAnswerMapT) => void;
  showAnswerAsSelectLimit?: number;
  styleOverrides?: any;
  // Component is partially stateful in this mode. When answers in "state prop" is modified, the answers internal state will also be updated. Use Questions component to completely control the state.
  controlledAnswers?: boolean;
  showValidationErrors?: boolean;
  isComposeMode?: boolean;
  enableAutoFocusForRTE?: boolean;
  onAnswerChange?: (arg: {
    questionId: string;
    answer: string;
  }) => void;
};
type State = {
  answers: UserAnswerMapT;
  questions: QuestionsType;
};
export class StatefulQuestions extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      state: qnaState,
      showValidationErrors
    } = props;
    this.answers = qnaState.answers;
    this.questionnaireRef = new Map();
    this.currentAnimatingNode = null;
    const questions = showValidationErrors ? addShowErrorsToQuestions(qnaState.questions) : qnaState.questions;
    this.state = {
      answers: this.answers,
      questions: questions
    };
  }
  questionnaireRef: Map<string, HTMLElement>;
  currentAnimatingNode: HTMLElement | null;
  componentDidUpdate(prevProps: Props) {
    if (this.props.controlledAnswers && this.props.state !== prevProps.state) {
      const updatedAnswers = this.props.state.answers;
      this.answers = updatedAnswers;
      this.setState({
        answers: updatedAnswers
      });
    }
    if (this.props.showValidationErrors !== prevProps.showValidationErrors) {
      const qnaStateQuestions = this.props.state.questions;
      this.setState({
        questions: this.props.showValidationErrors ? addShowErrorsToQuestions(qnaStateQuestions) : qnaStateQuestions
      });
    }
  }
  answers: UserAnswerMapT;
  updateAnswers = () => {
    const {
      updateAnswers
    } = this.props;
    if (updateAnswers) {
      updateAnswers(this.answers);
    }
  };
  setAnswers = (answers: UserAnswerMapT) => {
    this.answers = answers;
    this.setState({
      answers
    }, this.updateAnswers);
  };
  registerQuestionNode = (questionId: string, node: HTMLElement | null) => {
    if (node) {
      this.questionnaireRef.set(questionId, node);
    } else {
      this.questionnaireRef.delete(questionId);
    }
  };
  scrollToQuestion = (questionId: string, options?: ScrollIntoViewOptions) => {
    const node = this.questionnaireRef.get(questionId);
    if (node) {
      // Always reset previous animation to restart it
      if (this.currentAnimatingNode) {
        this.currentAnimatingNode.style.animation = '';
      }
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        ...options
      });

      // Track current animating node and apply animation
      this.currentAnimatingNode = node;
      applyFocusAnimation(node);
      return true;
    }
    console.warn(`Question with id "${questionId}" not found in questionnaireRef`);
    return false;
  };
  render() {
    const {
      state: qnaState,
      showAnswerAsSelectLimit,
      styleOverrides,
      isComposeMode = false,
      enableAutoFocusForRTE
    } = this.props;
    const styles = merge({}, defaultQuestionStyles, styleOverrides);
    const topQuestionIds = getTopQuestionIds(this.props.state);
    const qnaRenderState = {
      ...qnaState,
      answers: {
        ...qnaState.answers,
        ...this.answers
      },
      questions: this.state.questions
    };
    return <QuestionStyleContext.Provider value={styles}>
        <QuestionList>
          {topQuestionIds.map(id => {
          return <Question key={id} questionId={id} state={qnaRenderState} updateAnswers={arg => {
            this.setAnswers(updateAnswers(this.answers, arg));
          }} selectAnswer={arg => {
            this.setAnswers(selectAnswer(this.answers, arg));
            this.props.onAnswerChange?.({
              questionId: arg.questionId,
              answer: arg.answer
            });
          }} removeAnswer={arg => {
            this.setAnswers(removeAnswer(this.answers, arg));
          }} updateNote={arg => {
            this.setAnswers(updateNote(this.answers, arg));
          }} showAnswerAsSelectLimit={showAnswerAsSelectLimit} isComposeMode={isComposeMode} enableAutoFocusForRTE={enableAutoFocusForRTE} registerQuestionNode={this.registerQuestionNode} />;
        })}
        </QuestionList>
      </QuestionStyleContext.Provider>;
  }
}